<script setup lang="tsx">
import PlusButton from '@/components/widgets/PlusButton.vue'
import type { StandardMethodMetaData, Audit, StandardMethod } from '@/models/api-generated'
import {
	daysUntilDate,
	getStatusIcon,
	formatLocale,
	getResponsibleIcon,
	getDateFormat,
	formatDate,
	hasPermissionOwn,
	isRowClick,
} from '@/services/utils'
import useMethodColumns from './columns'
import methodEditor from '@/stores/method-editor'
import auditStore from '@/stores/audit'
import sliderStore from '@/stores/slider'
import AuditComments from '@/components/audit/AuditComments.vue'
import Datepicker from '@vuepic/vue-datepicker'
import PrioritySelect from '@/components/widgets/PrioritySelect.vue'
import Rules from '@/components/rules/Rules.vue'
import NumericInput from '@/components/widgets/NumericInput.vue'
import AuditSelect from '@/components/audit/AuditSelect.vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import iconPath from '@/assets/images/jira-1.svg'
import {
	ConditionallyImplemented,
	Implemented,
	MethodReference,
	PermAllJiraRead,
	PermAllMeasureWrite,
	PermOwnJiraRead,
	PermOwnMeasureWrite,
	Ok,
	Remark,
	Defect,
	NoKind,
	type AuditCommentKind,
} from '@/models/enums-generated'
import MethodStatusSelect from '@/components/widgets/MethodStatusSelect.vue'
import CustomRow from '../custom_field/CustomRow.vue'
import { type CustomValueMap } from '@/services/custom'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import jiraCbStore from '@/stores/jira-checkboxes'
import IconButton from '@/components/shared/IconButton.vue'
import CommentReferencesContainer from '../comment_references/CommentReferencesContainer.vue'
import { useTheme } from '../user/useTheme'

const { theme } = useTheme()
const props = defineProps<{
	class: any
	finishedAudit?: Audit
	method: StandardMethod
	startsExpanded: boolean
}>()

const openURL = (url: string) => {
	window.open(url, '_blank')
}

const originalUpdatedAt = ref<Date | null>(null)
const hasBeenUpdated = ref(false)
const model = defineModel<StandardMethodMetaData>({ required: true })
const customValues = defineModel<CustomValueMap>('customValues')
const editor = methodEditor()
const audit = auditStore()
const jiraCb = jiraCbStore()
const slider = sliderStore()
const { columns, custom } = useMethodColumns()
const activeAudits = computed(() => audit.activeBasic.filter((a) => !a.instance || a.instance.id === model.value.instance.id))
const activeInternalAudits = computed(() => audit.activeInternal.filter((a) => !a.instance || a.instance.id === model.value.instance.id))
const activeForeignInternalAudits = computed(() =>
	audit.activeForeignInternal.filter((a) => !a.instance || a.instance.id === model.value.instance.id),
)
const auditColspan = computed(() => (isAudit.value ? '6%' : '100%'))
const finishedAudits = computed(() => audit.old.filter((a) => !a.instance || a.instance.id === model.value.instance.id))
const selectedFinishedAudit = ref<Audit | undefined>(props.finishedAudit)
const isAudit = computed(
	() =>
		!editor.isEditMode &&
		activeAudits.value.length + activeInternalAudits.value.length + activeForeignInternalAudits.value.length + finishedAudits.value.length >
			0 &&
		columns.audit.value.isVisible,
)

const daysToDueDate = computed(() => {
	if (model.value.dueDate) {
		return daysUntilDate(model.value.dueDate)
	}
	return null
})

function normalizeKind(kind: string | null | undefined): AuditCommentKind {
	return (kind as AuditCommentKind) || NoKind
}

function handleCommentChange(payload: string | { oldKind: string | null; newKind: string }) {
	if (!model.value.auditChecks) {
		model.value.auditChecks = { ok: 0, remark: 0, defect: 0, noKind: 0, total: 0, inAudit: 0 }
	}

	if (typeof payload === 'string') {
		const kind = normalizeKind(payload)
		const newChecks = { ...model.value.auditChecks }
		newChecks.total++
		if (kind === Ok) newChecks.ok++
		else if (kind === Remark) newChecks.remark++
		else if (kind === Defect) newChecks.defect++
		else newChecks.noKind++
		model.value.auditChecks = newChecks
	} else {
		const { oldKind, newKind } = payload
		const normalizedOldKind = normalizeKind(oldKind)
		const normalizedNewKind = normalizeKind(newKind)
		const newChecks = { ...model.value.auditChecks }

		switch (normalizedOldKind) {
			case Ok:
				newChecks.ok--
				break
			case Remark:
				newChecks.remark--
				break
			case Defect:
				newChecks.defect--
				break
			case NoKind:
				newChecks.noKind--
				break
		}

		switch (normalizedNewKind) {
			case Ok:
				newChecks.ok++
				break
			case Remark:
				newChecks.remark++
				break
			case Defect:
				newChecks.defect++
				break
			case NoKind:
				newChecks.noKind++
				break
		}

		model.value.auditChecks = newChecks
	}
}

const emit = defineEmits(['checkedMassEditChange'])

const getFloat = (price: number): string => {
	return price.toFixed(2)
}

function clickMeta(e: MouseEvent) {
	if (!isRowClick(e)) return

	model.value.isExpanded = !model.value.isExpanded
}

const canEditImpl = computed(() => {
	if (isResponsibleChanged) return true

	return hasPermissionOwn(model.value.responsible, PermAllMeasureWrite, PermOwnMeasureWrite)
})

let isResponsibleChanged = false
watch(
	() => model.value.responsible,
	() => (isResponsibleChanged = true),
)

const canEditServiceProviderImpl = computed(() => {
	if (isServiceProviderChanged) return true

	return hasPermissionOwn(model.value.serviceProvider, PermAllMeasureWrite, PermOwnMeasureWrite)
})

let isServiceProviderChanged = false
watch(
	() => model.value.responsible,
	() => (isResponsibleChanged = true),
)

const canEdit = computed(() => {
	return editor.isEditMode && !jiraCb.isJiraMode && (canEditImpl.value || canEditServiceProviderImpl.value)
})

const statusIcon = computed(() => getStatusIcon(model.value.status, props.method.isRenewed))

function inputClass(reviewDate: string) {
	const today = new Date().toISOString()
	return { enabled: formatDate(reviewDate) === formatDate(today) }
}

const setCurrentTime = () => {
	if (!hasBeenUpdated.value) {
		originalUpdatedAt.value = model.value.updatedAt
		model.value.updatedAt = new Date()
		hasBeenUpdated.value = true
	} else {
		model.value.updatedAt = originalUpdatedAt.value
		hasBeenUpdated.value = false
	}
}

watch(
	() => audit.oldSelected,
	(newValue) => {
		if (newValue) {
			selectedFinishedAudit.value = newValue
		}
	},
)

onMounted(() => {
	nextTick(() => {
		if (props.startsExpanded) model.value.isExpanded = true
	})
})
</script>

<template>
	<tr :class="props.class" @click="clickMeta($event)">
		<td v-if="jiraCb.isJiraMode || (!editor.isEditMode && canEditImpl)">
			<input
				:id="'mass-edit-checkbox' + model.id"
				class="form-check-input"
				type="checkbox"
				v-model="model.isChecked"
				@change="emit('checkedMassEditChange', ($event.target as HTMLInputElement).checked)"
			/>
		</td>
		<td class="text-center" v-if="!props.startsExpanded"><PlusButton v-model="model.isExpanded"></PlusButton></td>
		<td style="max-width: 16em" :title="model.asset.name">
			<span v-if="props.method.module.type === 'P'">{{ $t('allBusinessProcesses') }}</span>
			<span v-else class="badge bg-primary me-2">A</span><span>{{ model.asset.name }}</span>
		</td>
		<td v-if="columns.instance.value.isVisible">{{ model.instance.name }}</td>
		<td v-if="columns.responsible.value.isVisible">
			<template v-if="!canEdit">
				<span v-if="!model.responsible">-</span>
				<span v-else class="me-2">
					<i :class="getResponsibleIcon(model.responsible)" alt="responsible"></i>
					{{ model.responsible.label }}
				</span>
			</template>
			<template v-else>
				<ResponsibleSelect :id="'responsible-select' + model.id" v-model="model.responsible" class="input-box"></ResponsibleSelect>
			</template>
		</td>
		<td v-if="columns.serviceProvider.value.isVisible">
			<template v-if="!canEdit">
				<span v-if="!model.serviceProvider">-</span>
				<span v-else class="me-2">
					<i class="bi-person-fill me-2 pie5" alt="service-provider"></i>
					{{ model.serviceProvider.label }}
				</span>
			</template>
			<template v-else>
				<ResponsibleSelect :id="'service-provider-select' + model.id" v-model="model.serviceProvider" class="input-box"></ResponsibleSelect>
			</template>
		</td>
		<td v-if="columns.priority.value.isVisible">
			<template v-if="!canEdit">
				<span v-if="model.priority">{{ $t(model.priority) }}</span>
				<span v-else>-</span>
			</template>
			<template v-else>
				<PrioritySelect :id="'priority-select' + model.id" v-model="model.priority"></PrioritySelect>
			</template>
		</td>
		<td v-if="columns.status.value.isVisible">
			<template v-if="!canEdit">
				<span class="icon-container me-2 ms-2" :title="statusIcon.title">
					<i :class="statusIcon.iconClass"></i>
					<i v-if="statusIcon.showAsterisk" class="asterisk-icon bi bi-asterisk text-white"></i>
				</span>
				<span data-test="method-status-text">{{ statusIcon.title }}</span>
			</template>
			<template v-else>
				<MethodStatusSelect :id="'method-status-select' + model.id" v-model="model.status" @focus="model.isExpanded = true">
				</MethodStatusSelect>
			</template>
		</td>
		<td v-if="columns.auditCheck.value.isVisible" class="text-center">
			<div v-if="model.auditChecks && model.auditChecks.total > 0" class="d-flex gap-1 justify-content-center">
				<span v-if="model.auditChecks.noKind > 0" v-one-tooltip="$t('audit.comment')">
					<i class="bi bi-chat-right-text me-1 text-primary"></i>{{ model.auditChecks.noKind }}
				</span>
				<span v-if="model.auditChecks.ok > 0" v-one-tooltip="$t('audit.ok')">
					<i class="bi bi-check-circle me-1 text-success"></i>{{ model.auditChecks.ok }}
				</span>
				<span v-if="model.auditChecks.remark > 0" v-one-tooltip="$t('audit.remark')">
					<i class="bi bi-exclamation-circle me-1 text-warning"></i>{{ model.auditChecks.remark }}
				</span>
				<span v-if="model.auditChecks.defect > 0" v-one-tooltip="$t('audit.defect')">
					<i class="bi bi-ban me-1 text-danger"></i>{{ model.auditChecks.defect }}
				</span>
			</div>

			<span v-else>-</span>
		</td>
		<td v-if="columns.price.value.isVisible">
			<template v-if="!canEdit">
				<span data-test="method-price-input">{{ getFloat(model.price) }}</span></template
			>
			<NumericInput v-else :id="'price-input' + model.id" class="form-control form-control-sm" v-model="model.price"></NumericInput>
		</td>

		<td v-if="columns.dueDate.value.isVisible">
			<template v-if="!canEdit">
				<span v-if="model.status === Implemented || model.status === ConditionallyImplemented">-</span>
				<span v-else>
					<span v-if="daysToDueDate !== null">
						<template v-if="daysToDueDate < 0">
							<span class="fw-bold" style="color: red">{{ $t('date.overdue') }}</span>
						</template>
						<template v-else-if="daysToDueDate <= 1">
							<span class="fw-bold" style="color: red">{{ daysToDueDate }} {{ $t('date.day') }}</span>
						</template>
						<template v-else-if="daysToDueDate > 1 && daysToDueDate < 3">
							<span class="fw-bold" style="color: red">{{ daysToDueDate }} {{ $t('date.day', 2) }}</span>
						</template>
						<template v-else-if="daysToDueDate >= 3 && daysToDueDate < 6">
							<span style="color: rgb(205, 135, 5)">{{ daysToDueDate }} {{ $t('date.day', 2) }}</span>
						</template>
						<template v-else>
							<span style="color: green">{{ daysToDueDate }} {{ $t('date.day', 2) }}</span>
						</template>
					</span>
					<span v-else>-</span>
				</span>
			</template>
			<template v-else>
				<Datepicker
					v-model="model.dueDate"
					:id="'date-select' + model.id"
					:clearable="true"
					:teleport="true"
					:enable-time-picker="false"
					:format="getDateFormat()"
					:format-locale="formatLocale()"
					:select-text="$t('misc.select')"
					:cancel-text="$t('misc.cancel')"
					text-input
					class="input-box mb-1"
					:dark="theme === 'dark'"
				/>
			</template>
		</td>

		<td v-if="columns.updatedAt.value.isVisible">
			<span v-if="!canEdit">{{ model.updatedAt ? formatDate(model.updatedAt) : '-' }}</span>
			<button v-else type="button" class="btn btn-outline-secondary" :class="inputClass(model.updatedAt)" @click="setCurrentTime()">
				{{ model.updatedAt ? formatDate(model.updatedAt) : '-' }}
			</button>
		</td>
		<td>
			<IconButton
				:id="'history-btn' + model.id"
				class="bi bi-book"
				:title="$t('history.label')"
				:size="canEdit ? '' : 'fs-6'"
				@mouseup="slider.openMethodHistory(model.id, props.method.name, model.asset.name)"
			>
			</IconButton>
		</td>

		<td v-if="columns.jira.value.isVisible">
			<span v-if="model.jiraUrl && hasPermissionOwn(model.responsible, PermAllJiraRead, PermOwnJiraRead)" class="me-2">
				<img
					:src="iconPath"
					:id="'jira-icon-btn' + model.id"
					alt="icon"
					class="image"
					v-one-tooltip="model.jiraUrl"
					@click="openURL(model.jiraUrl)"
				/>
			</span>
			<span v-else>-</span>
		</td>
		<CustomRow
			:columns="custom"
			v-model="customValues"
			:row-id="model.id"
			:is-edit="editor.isEditMode"
			table="standard_method_meta_data"
		></CustomRow>
	</tr>
	<tr v-if="model.isExpanded" class="bordered-rows">
		<td colspan="100%">
			<CommentReferencesContainer
				v-model="model.comment"
				:canEdit="canEdit"
				:url-references="model.urlReferences"
				:document-references="model.documentReferences"
				:owner-id="model.id"
				:owner-type="MethodReference"
			></CommentReferencesContainer>
		</td>
	</tr>
	<tr v-if="model.isExpanded" class="group-row bordered-rows bottom-border">
		<td :colspan="auditColspan">
			<div class="mb-3">
				<Rules v-model="model" :method="props.method" :is-read-only="!canEdit"></Rules>
			</div>
		</td>

		<template v-if="isAudit">
			<td colspan="50%">
				<div class="row">
					<div v-if="activeInternalAudits.length">
						<div class="text-center text-nowrap fw-bold">{{ $t('audit.internalComments') }}</div>
						<AuditComments
							v-for="(a, i) of activeInternalAudits"
							:key="a.id"
							v-model="activeInternalAudits[i]"
							:methodMetadataId="model.id"
							@comment-added="handleCommentChange"
						></AuditComments>
					</div>

					<div v-if="activeAudits.length">
						<div class="text-center text-nowrap fw-bold">{{ $t('audit.comment', 2) }}</div>
						<AuditComments
							v-for="(a, i) of activeAudits"
							:key="a.id"
							v-model="activeAudits[i]"
							:methodMetadataId="model.id"
							@comment-added="handleCommentChange"
						></AuditComments>
					</div>
					<div v-if="activeForeignInternalAudits.length">
						<div class="text-center text-nowrap fw-bold">{{ $t('audit.foreignInternalComments') }}</div>
						<AuditComments
							v-for="(a, i) of activeForeignInternalAudits"
							:key="a.id"
							v-model="activeForeignInternalAudits[i]"
							:methodMetadataId="model.id"
							@comment-added="handleCommentChange"
						></AuditComments>
					</div>

					<div v-if="finishedAudits.length">
						<div class="text-center text-nowrap fw-bold">{{ $t('audit.finishedAudits') }}</div>
						<div>
							<div class="ms-3 mt-1">
								<AuditSelect v-model="selectedFinishedAudit" :options="finishedAudits" :metadataId="model.id"></AuditSelect>
							</div>
							<AuditComments
								v-if="selectedFinishedAudit"
								v-model="selectedFinishedAudit"
								:methodMetadataId="model.id"
								@comment-added="handleCommentChange"
							></AuditComments>
						</div>
					</div>
				</div>
			</td>
		</template>
	</tr>
</template>

<style lang="sass" scoped>
tr.submethod > td
    cursor: pointer
    white-space: nowrap

tr.group-row > td
  cursor: default

td
  text-overflow: ellipsis
</style>
